import { forwardRef, useId, useMemo } from "react";

import { FormHelperText, useTheme } from "@mui/material";
import { InputLabel } from "components";
import { useTranslation } from "react-i18next";
// eslint-disable-next-line import/no-extraneous-dependencies
import { Mention, MentionsInput as ReactMentionsInput } from "react-mentions";

import {
  useFormulaBracketHighlight,
  useMentionsInput,
} from "./MentionsInput.hooks";
import {
  Container,
  getMentionStyle,
  getMentionsInputStyle,
} from "./MentionsInput.styles";
import {
  MentionsInputReference,
  MentionsInputProps,
} from "./MentionsInput.types";
import { createMentionDataProvider } from "./MentionsInput.utils";

const MentionsInput = forwardRef<MentionsInputReference, MentionsInputProps>(
  (
    {
      id,
      type = "default",
      label,
      placeholder,
      readOnly,
      required,
      defaultValue,
      value,
      helperText,
      disabled,
      error,
      inputRef,
      mentionData,
      suggestionsPortalHost,
      suggestionsSx,
      onChange,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const formId = useId();
    const { mentionsInputRef, valueState, onChangeHandler } = useMentionsInput({
      ref,
      defaultValue,
      value,
      onChange,
    });
    const mentionDataProvider = useMemo(
      () => createMentionDataProvider(mentionData),
      [mentionData]
    );
    const { onSelect, onBlur } = useFormulaBracketHighlight({
      enabled: type === "formula",
      value: valueState,
      mentionsInputRef,
    });

    return (
      <Container disabled={String(disabled)} error={String(error)}>
        {!!label && (
          <InputLabel htmlFor={id ?? formId} required={required}>
            {label}
          </InputLabel>
        )}
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <ReactMentionsInput
          ref={mentionsInputRef}
          inputRef={inputRef}
          id={id ?? formId}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder ?? t("component.inputText")}
          value={valueState}
          suggestionsPortalHost={suggestionsPortalHost ?? undefined}
          style={
            getMentionsInputStyle({
              theme,
              disabled,
              type,
              suggestionsSx,
            }) as never
          }
          allowSpaceInQuery
          onChange={onChangeHandler}
          onSelect={onSelect}
          onBlur={onBlur}
        >
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <Mention
            trigger="@"
            style={getMentionStyle({ theme })}
            data={mentionDataProvider}
            renderSuggestion={(entry, _highlightedDisplay, focused) => (
              <div className={`suggestion-item ${focused ? "focused" : ""}`}>
                <div>
                  {entry?.display}{" "}
                  <span style={{ fontWeight: 500 }}>
                    {entry?.id ? `(${entry?.id || ""})` : ""}{" "}
                  </span>
                </div>
              </div>
            )}
          />
        </ReactMentionsInput>
        {helperText ? (
          <FormHelperText required={required} error={error} disabled={disabled}>
            {helperText}
          </FormHelperText>
        ) : null}
      </Container>
    );
  }
);

export default MentionsInput;
